import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import type { RentPaymentPlanOption, RentPeriod } from "@/components/rent/rent-constants"
import { cn } from "@/lib/utils"

type RentPaymentPlanOptionsProps = {
  value: RentPeriod | ""
  onValueChange: (value: RentPeriod) => void
  options: RentPaymentPlanOption[]
  disabled?: boolean
  isLoading?: boolean
  errorMessage?: string | null
}

export function RentPaymentPlanOptions({
  value,
  onValueChange,
  options,
  disabled = false,
  isLoading = false,
  errorMessage = null,
}: RentPaymentPlanOptionsProps) {
  return (
    <section
      className={cn(
        "flex min-w-0 flex-col gap-4 transition-opacity",
        disabled && "pointer-events-none opacity-50",
      )}
      aria-disabled={disabled}
    >
      <h3 className="text-[18px] leading-[24px] lg:font-semibold text-foreground lg:text-[28px] lg:leading-[34px]">
        Как вам удобнее платить?
      </h3>

      {isLoading ? (
        <p className="text-sm text-light-muted">Загружаем тарифы...</p>
      ) : null}

      {errorMessage ? (
        <p className="text-sm text-destructive">{errorMessage}</p>
      ) : null}

      {!isLoading && !errorMessage && options.length === 0 ? (
        <p className="text-sm text-light-muted">Выберите дату заезда, чтобы увидеть тарифы</p>
      ) : null}

      {options.length > 0 ? (
        <RadioGroup
          value={value}
          onValueChange={(nextValue) => onValueChange(nextValue as RentPeriod)}
          className="grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-2 2xl:grid-cols-4"
          disabled={disabled || isLoading}
        >
          {options.map((option) => {
            const itemId = `rent-plan-${option.value}`

            return (
              <div key={option.value}>
                <RadioGroupItem id={itemId} value={option.value} className="peer sr-only absolute" />
                <Label
                  htmlFor={itemId}
                  className={cn(
                    "flex lg:min-h-[160px] min-h-[120px] cursor-pointer flex-col items-start justify-between rounded-[4px] border-2 border-transparent bg-background px-4 py-3",
                    "hover:border-brand hover:bg-card hover:[&_[data-slot=plan-badge]]:bg-background",
                    "peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-card peer-data-[state=checked]:pointer-events-none",
                    "peer-data-[state=checked]:[&_[data-slot=plan-badge]]:bg-background peer-data-[state=checked]:[&_[data-slot=discount-badge]]:bg-background",
                  )}
                >
                  <div className="flex items-center gap-1">
                    <Badge
                      data-slot="plan-badge"
                      className="h-auto rounded-[2px] bg-white px-1 py-0.5 text-[12px] leading-4 font-medium text-light-muted"
                    >
                      {option.badge}
                    </Badge>
                    {option.discountBadge ? (
                      <Badge
                        data-slot="discount-badge"
                        className="h-auto rounded-[4px] bg-white px-[6px] py-[2px] text-[12px] leading-4 font-medium text-pink lg:hidden"
                      >
                        {option.discountBadge}
                      </Badge>
                    ) : null}
                  </div>

                  <div className="flex w-full flex-col items-start gap-1">
                    {option.discountBadge ? (
                      <Badge
                        data-slot="discount-badge"
                        className="h-auto rounded-[4px] bg-white px-[6px] py-[2px] text-[12px] leading-4 font-medium text-pink hidden lg:block"
                      >
                        {option.discountBadge}
                      </Badge>
                    ) : null}
                    <span className="text-[24px] lg:leading-9 leading-[20px] font-semibold text-foreground">
                      {option.mainPriceLabel}
                    </span>
                    <span className="text-[18px] leading-6 text-light-muted">
                      {option.totalLabel}
                    </span>
                  </div>
                </Label>
              </div>
            )
          })}
        </RadioGroup>
      ) : null}
    </section>
  )
}
